import React, {useCallback, useEffect, useMemo} from "react";
import Button from "../../../../../../../base/button/button";
import {Col, Row} from "reactstrap";
import {Box, createFilterOptions, Tooltip, Typography} from "@mui/material";
import classnames from "classnames";
import ProductInformationPopover from "../../../../../../../app-specific/draggable-popover/product-information-popover";
import {EditPartSupportRequestProcessSteps} from "../../../../index";
import PartAutocomplete from "../../../../../../../text-fields/autocomplete/part-autocomplete";

const EditPartSupportRequestProductInfoSectionBaseSection = ({
                                                                 request,
                                                                 productInfo,
                                                                 setProductInfo,
                                                                 setProcess,
                                                                 warehouse,
                                                                 warehouseChanged
                                                             }) => {
    const haveChangesInPartNoSelection = useMemo(() =>
            !!productInfo?.part ||
            !!productInfo?.serialNumber,
        [productInfo])

    /**
     * As soon as the component mounts:
     * - focuses the auto complete component for editing.
     */
    useEffect(() => {
        document.getElementById('focusable')?.focus();
    }, [])

    /**
     * Based on the available data decides what is the appropriate next step:
     * - if there is no change, continues to next step
     * - if the serial number is filled, continues to next step
     * - in other cases (part is filled), shows the invoice views.
     */
    const onContinueAfterPartSelection = () => {
        if (warehouseChanged && !haveChangesInPartNoSelection) {
            document.getElementById('focusable')?.focus();
            return;
        }
        if (!haveChangesInPartNoSelection) {
            setProductInfo({
                part: {partNumber: request.partNo},
                isSerialized: !!request.serialNo,
                showProductOrSerialNoSelection: false,
                serialNumber: request.serialNo,
                selectedQuantity: request.unSeriliazablePartsCount,
                invoiceNo: request.invoiceNo,
                warehouseCode: request.warehouseCode,
            })
            return setProcess(EditPartSupportRequestProcessSteps.review)
        }
        if (!!productInfo.serialNumber?.length) {
            setProductInfo({
                part: {partNumber: request.partNo},
                isSerialized: true,
                showProductOrSerialNoSelection: true,
                serialNumber: productInfo.serialNumber,
                selectedQuantity: undefined,
                invoiceNo: request.invoiceNo,
                warehouseCode: request.warehouseCode,
            })
            return setProcess(EditPartSupportRequestProcessSteps.review)
        }
        setProductInfo(prevState => ({
            ...prevState,
            showProductOrSerialNoSelection: true,
        }))
    }

    /**
     * Changes the look-up value for serial number and nullifies the part only if the serial number is an un-empty
     * @param partOrSerial
     * @returns {*}
     */
    const onNewPartSelected = useCallback((partOrSerial) => {
        // serial no
        if (typeof partOrSerial === 'string') {
            return setProductInfo(previousState => ({
                ...previousState,
                part: null,
                serialNumber: partOrSerial,
                isSerialized: true,
            }))
        }
        setProductInfo(previousState => ({
            ...previousState,
            part: !partOrSerial ? partOrSerial : {...partOrSerial, partNo: partOrSerial.partNo ?? partOrSerial.partNumber, partNumber: partOrSerial.partNumber ?? partOrSerial.partNo},
            serialNumber: null,
            isSerialized: false,
        }))
    }, [setProductInfo])

    /**
     * Default render function for customer options
     * Displays a two-column layout with name and city
     *
     * @param props - HTML attributes for the list item
     * @param option - Customer option to render
     */
    const renderOption = useCallback((props, option) => {
        const {key: _, ...otherProps} = props;
        const isFreeSoloValue = typeof option === 'string';
        return (
            <Box component="li" {...otherProps} sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                {
                    isFreeSoloValue &&
                    <p className='autocomplete-free-solo-button'>
                        Serial No: {option}
                    </p>
                }
                {
                    !isFreeSoloValue &&
                    (option.partNumber ?? option.partNo ?? '')
                }
            </Box>
        );
    }, [])

    const defaultFilterOptions = useMemo(() => createFilterOptions(), [])
    /**
     * Filters the autocomplete options based on the input value.
     * @type {function(*, *): unknown[]}
     */
    const filterOptions = useCallback((options, params) => {
        const filtered = defaultFilterOptions(options, params);
        if (!!params.inputValue?.trim() && !options.some(option => (option.partNo ?? option.partNumber).toLowerCase()?.trim() === params.inputValue.toLowerCase()?.trim())) {
            filtered.push(params.inputValue.trim());
        }
        return filtered;
    }, [defaultFilterOptions])

    return (
        <>
            <Col className={'flex-grow-1 pl-5'}>
                <p className={'text-md font-weight-semi-bold mb-2'}>
                    Current Product
                </p>
                <div className={'edit-psq-card part-number-fluid'}>
                    <p>
                        Part No: <ProductInformationPopover partNo={request?.partNo} wrapperProps={{sx: {display: 'inline-block'}}}>
                        <Typography
                            component={'span'}
                            sx={theme => ({color: `${theme.palette.primary.main} !important`})}
                        >
                            {request?.partNo ?? ""}
                        </Typography>
                    </ProductInformationPopover>
                    </p>
                    {
                        !!warehouse
                            ? <p className={classnames({'new': warehouseChanged})}>
                                Warehouse: <span>{warehouse?.name ?? '--'}</span>
                            </p>
                            : <p>
                                Invoice No: <span>{request?.invoiceNo ?? '--'}</span>
                            </p>
                    }
                    <p>
                        {
                            !request?.unSeriliazablePartsCount
                                ? <>Serial No: <span>{request?.serialNo ?? '--'}</span></>
                                : <>Qty: <span>{request?.unSeriliazablePartsCount ?? '--'}</span></>
                        }
                    </p>
                </div>
            </Col>
            <Col className={'flex-grow-1 pr-5 mt-4 pt-2'}>
                <PartAutocomplete
                    autoFocus
                    fullWidth
                    freeSolo
                    label={"Product / Serial No"}
                    value={(!!productInfo?.part && productInfo?.part?.id !== undefined ? productInfo?.part : productInfo.serialNumber) ?? ""}
                    onChange={(_, value) => onNewPartSelected(value)}
                    getOptionLabel={v => typeof v === "string" ? v : v?.partNumber ?? v?.partNo ?? ''}
                    renderOption={renderOption}
                    filterOptions={filterOptions}
                />
            </Col>
            <div className={'w-100 my-5'}/>
            <Row className='no-gutters footer justify-content-between align-items-start w-100'>
                <Button
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => setProcess(EditPartSupportRequestProcessSteps.basicInfo)}
                    borderRadius={'0 8px 0 8px'}
                >
                    Back
                </Button>
                <Tooltip
                    arrow
                    placement={'top'}
                    title={warehouseChanged && !haveChangesInPartNoSelection
                        ? 'please select a product to continue'
                        : ''
                    }>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={onContinueAfterPartSelection}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            warehouseChanged && !haveChangesInPartNoSelection
                                ? "continue"
                                : haveChangesInPartNoSelection
                                    ? "Continue"
                                    : `Continue without any Changes`
                        }
                    </Button>
                </Tooltip>
            </Row>
        </>
    );
}
export default EditPartSupportRequestProductInfoSectionBaseSection;
